import React from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import OlcDeleteIcon from '~/components/General/OlcDeleteIcon';
import OlcEditIcon from '~/components/General/OlcEditIcon';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayCircleOutline';
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const ACTION_ICONS = {
  add: AddRoundedIcon,
  archive: ArchiveOutlinedIcon,
  back: ArrowBackRoundedIcon,
  confirm: CheckRoundedIcon,
  copy: ContentCopyOutlinedIcon,
  close: CloseRoundedIcon,
  card: CreditCardOutlinedIcon,
  delete: OlcDeleteIcon,
  download: FileDownloadOutlinedIcon,
  edit: OlcEditIcon,
  filter: FilterAltOutlinedIcon,
  forward: ArrowForwardRoundedIcon,
  invite: PersonAddAltOutlinedIcon,
  link: LinkRoundedIcon,
  login: LoginRoundedIcon,
  payment: PaymentsOutlinedIcon,
  person: PersonOutlineRoundedIcon,
  play: PlayArrowRoundedIcon,
  power: PowerSettingsNewRoundedIcon,
  refresh: RefreshRoundedIcon,
  reorder: SwapVertRoundedIcon,
  save: SaveOutlinedIcon,
  search: SearchRoundedIcon,
  send: SendOutlinedIcon,
  unlock: LockOpenOutlinedIcon,
  upgrade: TrendingUpRoundedIcon,
  downgrade: TrendingDownRoundedIcon,
  upload: FileUploadOutlinedIcon,
  verify: VerifiedOutlinedIcon,
  view: VisibilityOutlinedIcon,
  purchase: ShoppingCartOutlinedIcon,
};

const ACTION_RULES = [
  [/(^|\s)(back|previous|switch back)(\s|$)/, 'back'],
  [/(^|\s)(remove funds)(\s|$)/, 'payment'],
  [/(^|\s)(cancel|close)(\s|$)/, 'close'],
  [/(^|\s)(delete|remove|discard)(\s|$)/, 'delete'],
  [/(^|\s)(add payment methods?|payment method|card)(\s|$)/, 'card'],
  [/(^|\s)archive(\s|$)/, 'archive'],
  [/(^|\s)(duplicate|copy|clone)(\s|$)/, 'copy'],
  [/(^|\s)(download|export)(\s|$)/, 'download'],
  [/(^|\s)(upload|import|select files?)(\s|$)/, 'upload'],
  [/(^|\s)(search|find)(\s|$)/, 'search'],
  [/(^|\s)(filter|filters)(\s|$)/, 'filter'],
  [
    /(^|\s)(refresh|retry|try again|recheck|resubscribe|rotate|regenerate|reload|reopen)(\s|$)/,
    'refresh',
  ],
  [/(^|\s)(edit|rename)(\s|$)/, 'edit'],
  [/(^|\s)invite(\s|$)/, 'invite'],
  [/(^|\s)(send|resend|dispatch)(\s|$)/, 'send'],
  [/(^|\s)(sign in|log in|login)(\s|$)/, 'login'],
  [/(^|\s)(purchase|checkout|place order)(\s|$)/, 'purchase'],
  [/(^|\s)(fund|funds|payment|pay)(\s|$)/, 'payment'],
  [/(^|\s)upgrade(\s|$)/, 'upgrade'],
  [/(^|\s)downgrade(\s|$)/, 'downgrade'],
  [/(^|\s)(view|preview)(\s|$)/, 'view'],
  [/(^|\s)(test|run|process|use loom)(\s|$)/, 'play'],
  [/(^|\s)(activate|deactivate|enable|disable)(\s|$)/, 'power'],
  [/(^|\s)(verify|verification)(\s|$)/, 'verify'],
  [/(^|\s)(unlock)(\s|$)/, 'unlock'],
  [/(^|\s)(connect)(\s|$)/, 'link'],
  [/(^|\s)(impersonate)(\s|$)/, 'person'],
  [/(^|\s)(rearrange|reorder|sort)(\s|$)/, 'reorder'],
  [/(^|\s)(save|update|change)(\s|$)/, 'save'],
  [/(^|\s)(submit|confirm|approve|yes|ok|okay|done|finish|complete|apply)(\s|$)/, 'confirm'],
  [/(^|\s)(next|continue|proceed|start)(\s|$)/, 'forward'],
  [/(^|\s)(create|add|new)(\s|$)/, 'add'],
];

const getButtonText = (children) =>
  React.Children.toArray(children)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (React.isValidElement(child)) return getButtonText(child.props.children);
      return '';
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getAutomaticIconKey = (children) => {
  const label = getButtonText(children);
  if (!label) return null;

  const rule = ACTION_RULES.find(([pattern]) => pattern.test(label));
  return rule?.[1] || 'confirm';
};

const renderIcon = (iconKey) => {
  const IconComponent = ACTION_ICONS[iconKey] || ACTION_ICONS.confirm;
  return <IconComponent fontSize="inherit" />;
};

export const getOlcButtonIcon = ({ children, icon, startIcon, variant }) => {
  if (variant !== 'primary') return null;
  if (icon === false || icon === 'none') return null;

  if (React.isValidElement(icon)) return icon;
  if (typeof icon === 'string') return renderIcon(icon);
  if (React.isValidElement(startIcon)) return startIcon;

  const automaticIconKey = getAutomaticIconKey(children);
  return automaticIconKey ? renderIcon(automaticIconKey) : null;
};

export const OLC_BUTTON_ICON_KEYS = Object.freeze(Object.keys(ACTION_ICONS));
